import { Button, css, FormField, Heading, Modal, SnapshotIcon, styled, TextInput } from 'canoe';
import { Field, FieldInputProps, FieldMetaProps, Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { useExtensionInstallationState } from 'dashboard/containers/ExtensionStateProvider';
import { NewSnapshotModalExtensionPrompt } from 'dashboard/pages/Content/pages/Snapshots/NewSnapshotModalExtensionPrompt';

const validationSchema = Yup.object().shape({
  url: Yup.string()
    .required()
    .matches(/^(https?:\/\/)?.*\..+(\..+)*/, 'Enter a valid URL'),
});

const FormRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spaces.xs};
  `}
`;

interface Props {
  onClose: VoidFunction;
  onSubmit: (values: { url: string }) => void;
  snapshotURL: string;
}

export const RetakeSnapshotModal = ({ onClose, onSubmit, snapshotURL }: Props) => {
  const [stepIndex, setStepIndex] = useState(0);
  const { hasInstalledChromeExtension } = useExtensionInstallationState();

  return (
    <Modal onClose={onClose} closeOnClickOutside>
      <Modal.Body>
        <Formik<{ url: string }>
          validateOnMount
          initialValues={{
            url: snapshotURL,
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ isSubmitting, isValid, handleSubmit }) => (
            <>
              {stepIndex === 0 && (
                <>
                  <Heading size="S" align="center" mt="normal" mb="normal">
                    Snapshot URL
                  </Heading>
                  <Form
                    data-cy="retake-snapshot-form"
                    onKeyDown={(e: KeyboardEvent) => {
                      if (isValid && e.key === 'Enter') {
                        handleSubmit();
                      }
                    }}
                  >
                    <FormRow>
                      <Field name="url">
                        {({
                          field,
                          meta,
                        }: {
                          field: FieldInputProps<string>;
                          meta: FieldMetaProps<string>;
                        }) => (
                          <FormField
                            feedback={meta.touched ? meta.error : ''}
                            state={meta.touched && meta.error ? 'danger' : 'none'}
                          >
                            <TextInput {...field} {...meta} placeholder="your-app.io/dashboard" />
                          </FormField>
                        )}
                      </Field>

                      <Button
                        variant="secondary"
                        icon={SnapshotIcon}
                        loading={isSubmitting}
                        disabled={!isValid}
                        mt="xs"
                        onClick={() => {
                          if (hasInstalledChromeExtension) {
                            handleSubmit();
                          } else {
                            setStepIndex(1);
                          }
                        }}
                      >
                        Retake Snapshot
                      </Button>
                    </FormRow>
                  </Form>
                </>
              )}

              {stepIndex === 1 && (
                <NewSnapshotModalExtensionPrompt
                  onClose={onClose}
                  onSubmit={() => handleSubmit()}
                />
              )}
            </>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
};
